import ReferralCheckButton from '@atoms/ReferralCheckButton';
import ReferralDoneButton from '@atoms/ReferralDoneButton';
import ReferralUserInput from '@atoms/ReferralUserInput';
import ReferralValidatedInput from '@atoms/ReferralValidatedInput';
import { useToast } from '@hooks';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';
import { showSuccessToast } from '@utils/toastManager';
import { TFunction } from 'i18next';
import React from 'react';

interface ToastSuccessProps {
  isSuccess: boolean;
  data: unknown;
  toast: ReturnType<typeof useToast>;
  setReferralValidated: (value: boolean) => void;
  setValidatedReferralCode: (code: string) => void;
  referralCode: string;
  pageSource: string;
  t: TFunction<'common'>;
}

interface ShowValidatedButtonProps {
  referralValidated: boolean;
  referralCode: string;
  validateReferralCodeApi: (code: string) => void;
  pageSource: string;
}

interface ShowValidatedReferralInputProps {
  t: TFunction<'common'>;
  referralValidated: boolean;
  setReferralCode: (code: string) => void;
  validatedReferralCode: string;
  pageSource: string;
}

export function shouldShowToastOnSuccess({
  isSuccess,
  data,
  toast,
  setReferralValidated,
  setValidatedReferralCode,
  referralCode,
  pageSource,
  t,
}: ToastSuccessProps): void {
  if (isSuccess && typeof data === 'string' && data === 'Valid Referral Code') {
    showSuccessToast(toast, t('referral.validCodeText'), 'referralValidCode');
    setReferralValidated(true);

    const e = AMPLITUDE_CONSTANTS.redeemptionFlow.referral_code_successful;
    AmplitudeHelper.logEvent(e.name, {
      [e.attributes.referral_code]: referralCode ?? '',
      [e.attributes.source]: pageSource,
    });

    setValidatedReferralCode(referralCode);
  }
}

export function shouldShowValidatedButton({
  referralValidated,
  referralCode,
  validateReferralCodeApi,
  pageSource,
}: ShowValidatedButtonProps): React.JSX.Element {
  if (referralValidated) {
    return <ReferralDoneButton />;
  }
  return (
    <ReferralCheckButton
      referralCode={referralCode}
      validateReferralCodeApi={validateReferralCodeApi}
      pageSource={pageSource}
    />
  );
}

export function shouldShowValidatedReferralInput({
  referralValidated,
  setReferralCode,
  validatedReferralCode,
  pageSource,
}: ShowValidatedReferralInputProps): React.JSX.Element {
  if (referralValidated) {
    return <ReferralValidatedInput validatedReferralCode={validatedReferralCode} />;
  }
  return <ReferralUserInput setReferralCode={setReferralCode} pageSource={pageSource} />;
}
